import { useState, useEffect, useCallback } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Plus, X, User, Edit } from "lucide-react";
import { instrumentOptions, tierOptions, performerTypes } from "@shared/schema";
import type { Performer, Instrument, PerformerWithParticipant, JamNight } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface ParticipantEditModalProps {
  performer: PerformerWithParticipant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}


// Simple debounce helper
function debounce<T extends (...args: any[]) => any>(func: T, wait: number): T {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
}

export default function ParticipantEditModal({
  performer,
  open,
  onOpenChange,
  jamNightId,
}: ParticipantEditModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch jam night details to get event type
  const { data: jamNight } = useQuery<JamNight>({
    queryKey: [`/api/events/${jamNightId}`],
    enabled: open, // Only fetch when modal is open
  });
  
  // Local form state
  const [formData, setFormData] = useState({
    name: performer.name,
    priority: performer.priority || false,
    tier: performer.tier || "",
    instruments: performer.instruments || [],
    performerType: performer.performerType || "solo",
    notes: performer.notes || "",
    phone: performer.phone || "",
    email: performer.email || "",
    instagram: performer.instagram || "",
    lookingForBand: performer.participant?.lookingForBand || false,
  });

  // Reset form data when performer changes or modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: performer.name,
        priority: performer.priority || false,
        tier: performer.tier || "",
        instruments: performer.instruments || [],
        performerType: performer.performerType || "solo",
        notes: performer.notes || "",
        phone: performer.phone || "",
        email: performer.email || "",
        instagram: performer.instagram || "",
        lookingForBand: performer.participant?.lookingForBand || false,
      });
    }
  }, [performer, open]);

  const updatePerformerMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Performer>;
    }) => {
      const response = await apiRequest("PATCH", `/api/performers/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update performer",
        variant: "destructive",
      });
    },
  });

  const updateParticipantMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number;
      updates: { lookingForBand?: boolean };
    }) => {
      const response = await apiRequest("PATCH", `/api/participants/${id}`, updates);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/performers?jamNightId=${jamNightId}`],
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update participant",
        variant: "destructive",
      });
    },
  });

  // Debounced update function
  const debouncedUpdate = useCallback(
    debounce((field: keyof Performer, value: any) => {
      updatePerformerMutation.mutate({
        id: performer.id,
        updates: { [field]: value },
      });
    }, 500),
    [performer.id]
  );

  // Debounced participant update function
  const debouncedParticipantUpdate = useCallback(
    debounce((field: 'lookingForBand', value: boolean) => {
      if (performer.participant) {
        updateParticipantMutation.mutate({
          id: performer.participant.id,
          updates: { [field]: value },
        });
      }
    }, 500),
    [performer.participant?.id]
  );

  const handleFieldUpdate = (
    field: keyof Performer,
    value: any
  ) => {
    // Update local state immediately
    setFormData(prev => ({ ...prev, [field]: value }));
    // Debounce the API call
    debouncedUpdate(field, value);
  };

  const handleParticipantFieldUpdate = (
    field: 'lookingForBand',
    value: boolean
  ) => {
    // Update local state immediately
    setFormData(prev => ({ ...prev, [field]: value }));
    // Debounce the API call
    debouncedParticipantUpdate(field, value);
  };


  const handleInstrumentToggle = (
    instrument: Instrument
  ) => {
    const currentInstruments = formData.instruments;
    const newInstruments = currentInstruments.includes(instrument)
      ? currentInstruments.filter((i) => i !== instrument)
      : [...currentInstruments, instrument];

    handleFieldUpdate("instruments", newInstruments);
  };

  // Check event types
  const isGenericEvent = jamNight?.type === "generic";
  const isOpenMicEvent = jamNight?.type === "openmic";
  const isJamEvent = jamNight?.type === "jam";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Participant - {formData.name}
            </DialogTitle>
          </DialogHeader>

          {/* Hidden autofocus element to prevent name field auto-selection */}
          <button autoFocus style={{ opacity: 0, position: 'absolute', pointerEvents: 'none', width: 0, height: 0 }} />
          
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Name
              </label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  handleFieldUpdate("name", e.target.value)
                }
                placeholder="Participant name"
              />
            </div>

            {/* Show priority status for jam and open mic events */}
            {!isGenericEvent && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Priority Status
                </label>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.priority}
                    onCheckedChange={(checked) =>
                      handleFieldUpdate("priority", checked)
                    }
                  />
                  <label className="text-sm text-slate-600">
                    {formData.priority
                      ? "Priority Participant"
                      : "Regular Participant"}
                  </label>
                </div>
              </div>
            )}

            {/* Jam Night specific fields */}
            {isJamEvent && (
              <>
                {/* Tier */}
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">
                    Tier
                  </label>
                  <Select
                    value={formData.tier}
                    onValueChange={(value) =>
                      handleFieldUpdate("tier", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent>
                      {tierOptions.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Instruments */}
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-2">
                    Instruments
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-3 bg-slate-100 text-slate-600 hover:bg-slate-200"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Instrument
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56">
                        <div className="space-y-2">
                          <div className="text-sm font-medium">
                            Select Instruments:
                          </div>
                          {instrumentOptions.map((instrument) => (
                            <div
                              key={instrument}
                              className="flex items-center space-x-2"
                            >
                              <Checkbox
                                checked={formData.instruments.includes(instrument)}
                                onCheckedChange={() =>
                                  handleInstrumentToggle(instrument)
                                }
                              />
                              <label className="text-sm capitalize">
                                {instrument}
                              </label>
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {formData.instruments.map((instrument) => (
                      <Badge
                        key={instrument}
                        variant="secondary"
                        className="bg-blue-100 text-blue-800"
                      >
                        {instrument}
                        <button
                          onClick={() =>
                            handleInstrumentToggle(instrument as Instrument)
                          }
                          className="ml-1 text-blue-600 hover:text-blue-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Open Mic specific fields */}
            {isOpenMicEvent && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Performer Type
                </label>
                <Select
                  value={formData.performerType}
                  onValueChange={(value) =>
                    handleFieldUpdate("performerType", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select performer type" />
                  </SelectTrigger>
                  <SelectContent>
                    {performerTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type === "solo" ? "Solo" : "Band"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-1">
                Notes
              </label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  handleFieldUpdate("notes", e.target.value)
                }
                placeholder="Add notes about this performer..."
                className="min-h-[100px] resize-none"
                rows={4}
              />
            </div>

            {/* Contact Details */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-700 border-b border-slate-200 pb-2">
                Contact Details
              </h3>
              
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Phone Number
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    handleFieldUpdate("phone", e.target.value)
                  }
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    handleFieldUpdate("email", e.target.value)
                  }
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">
                  Instagram Handle
                </label>
                <Input
                  value={formData.instagram}
                  onChange={(e) =>
                    handleFieldUpdate("instagram", e.target.value)
                  }
                  placeholder="Enter Instagram handle"
                />
              </div>

              {performer.participant && (
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-2">
                    Looking for a Band
                  </label>
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.lookingForBand}
                      onCheckedChange={(checked) =>
                        handleParticipantFieldUpdate("lookingForBand", checked)
                      }
                    />
                    <label className="text-sm text-slate-600">
                      {formData.lookingForBand
                        ? "Looking for a band"
                        : "Not looking for a band"}
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Done Button */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <Button
              onClick={() => onOpenChange(false)}
              className="bg-blue-600/80 backdrop-blur-sm text-white hover:bg-blue-700/90 border border-blue-500/30 shadow-lg transition-all duration-300"
              style={{
                boxShadow: '0 4px 16px rgba(59,130,246,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }}
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
}